import { useMemo } from 'react';
import type {
  SatelliteWebLandlordUserWarningsResponseDto,
  SatelliteWebRenterUserWarningsResponseDto,
} from '@zg-rentals/ts-satellite-client';
import type { ExternalLinkWarningData } from '../components/modals/ExternalLinkWarningModal';
import type { PersonalContactDetailsWarningData } from '../components/modals/PersonalContactDetailsWarningModal';

// Use a union of both response types so the hook works for landlord and renter
// Make fields optional to handle cases where API returns partial data
export type RawUserWarningsData = Partial<
  SatelliteWebLandlordUserWarningsResponseDto & SatelliteWebRenterUserWarningsResponseDto
>;

export type AttachmentSafetySystemMessageData = {
  disclaimerText: string;
  linkText: string;
  linkUrl: string;
};

export type AttachmentInterstitialWarningData = {
  title: string;
  body: string;
  linkText: string;
  linkUrl: string;
  primaryCtaLabel: string;
  secondaryCtaLabel: string;
};

export type ConversationThreadWarningData = {
  disclaimerText: string;
  linkText: string;
  linkUrl: string;
  linkSuffix: string;
};

export type UseUserWarningsOptions = {
  enablePersonalContactWarning?: boolean;
  enableExternalLinkWarning?: boolean;
  userWarningsData?: RawUserWarningsData;
};

export type UseUserWarningsReturn = {
  personalContactWarningData?: PersonalContactDetailsWarningData;
  externalLinkWarningData?: ExternalLinkWarningData;
  attachmentSafetySystemMessageData?: AttachmentSafetySystemMessageData;
  attachmentInterstitialWarningData?: AttachmentInterstitialWarningData;
  conversationThreadWarningData?: ConversationThreadWarningData;
};

/**
 * Shared hook to process user warnings data
 * Transforms raw API response into component-ready data structures
 * This hook does NOT fetch data - it only processes data passed to it
 */
export const useSharedUserWarnings = ({
  enablePersonalContactWarning = true,
  enableExternalLinkWarning = true,
  userWarningsData,
}: UseUserWarningsOptions): UseUserWarningsReturn => {
  const personalContactWarningData = useMemo(() => {
    if (!enablePersonalContactWarning || !userWarningsData?.personalContactDetailsWarning) {
      return undefined;
    }

    const warning = userWarningsData.personalContactDetailsWarning;
    return {
      title: warning.disclaimerTitle,
      text: warning.disclaimerText,
      linkText: warning.link?.text,
      linkUrl: warning.link?.url,
      primaryButtonLabel: warning.primaryCta?.label,
      primaryButtonType: warning.primaryCta?.type as 'Button' | 'TextButton',
      secondaryButtonLabel: warning.secondaryCta?.label,
      secondaryButtonType: warning.secondaryCta?.type as 'Button' | 'TextButton',
    };
  }, [enablePersonalContactWarning, userWarningsData]);

  const externalLinkWarningData = useMemo(() => {
    if (!enableExternalLinkWarning || !userWarningsData?.externalLinkWarning) {
      return undefined;
    }

    const warning = userWarningsData.externalLinkWarning;
    return {
      disclaimerTitle: warning.disclaimerTitle,
      disclaimerText: warning.disclaimerText,
      primaryCta: warning.primaryCta,
      allowListedDomains: warning.allowListedDomains,
    };
  }, [enableExternalLinkWarning, userWarningsData]);

  const attachmentSafetySystemMessageData = useMemo(() => {
    if (!userWarningsData?.attachmentSafetySystemMessage) {
      return undefined;
    }

    const warning = userWarningsData.attachmentSafetySystemMessage;
    return {
      disclaimerText: warning.disclaimerText,
      linkText: warning.link.text,
      linkUrl: warning.link.url,
    };
  }, [userWarningsData]);

  const attachmentInterstitialWarningData = useMemo(() => {
    if (!userWarningsData?.attachmentInterstitialWarning) {
      return undefined;
    }

    const warning = userWarningsData.attachmentInterstitialWarning;
    return {
      title: warning.disclaimerTitle,
      body: warning.disclaimerText,
      linkText: warning.link.text,
      linkUrl: warning.link.url,
      primaryCtaLabel: warning.primaryCta.label,
      secondaryCtaLabel: warning.secondaryCta.label,
    };
  }, [userWarningsData]);

  const conversationThreadWarningData = useMemo(() => {
    if (!userWarningsData?.conversationThreadWarning) {
      return undefined;
    }

    const warning = userWarningsData.conversationThreadWarning;
    return {
      disclaimerText: warning.disclaimerText,
      linkText: warning.link.text,
      linkUrl: warning.link.url,
      linkSuffix: warning.linkSuffix,
    };
  }, [userWarningsData]);

  return {
    personalContactWarningData,
    externalLinkWarningData,
    attachmentSafetySystemMessageData,
    attachmentInterstitialWarningData,
    conversationThreadWarningData,
  };
};
